import {
	Box,
	Checkbox,
	createStyles,
	FileInput,
	Group,
	Radio,
	Stack,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useSuspenseApiMemberEnv } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { SettingsBlock } from '@repo/common/components';
import {
	type IntegrationSchemaKeys,
	integrationSchemas,
} from '@repo/common/constants/integration/integrations.schemas';
import {
	Banner,
	Button,
	NumberInput,
	Text,
	TextArea,
	TextInput,
	TextInputLabel,
	Title,
} from '@repo/foundations';
import { useFormik } from 'formik';
import {
	capitalize,
	isEmpty,
	isNil,
	keys,
	omit,
	omitBy,
	reverse,
} from 'lodash-es';
import {
	Suspense,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState,
} from 'react';
import { useNavigate } from 'react-router';
import type { AnySchema, ObjectSchema } from 'yup';
import * as Yup from 'yup';
import { api } from '../../../../../../network';
import type { IIntegration } from '../../../../../api';
import {
	integrationsQueryKeyFactory,
	queryClient,
	useAuthUser,
	useCreateIntegration,
	useIntegrationList,
	useSuspenseFeatureAccess,
	useUpdateIntegration,
} from '../../../../../api';
import { apiClient } from '../../../../../api/common';
import type {
	BuiltinIntegrationSpec,
	IntegrationSpec,
	SlackIntegrationSpec,
} from '../../../../../interfaces/IntegrationSpec';
import { useFeatureFlags } from '../../../../../utils/featureFlags';
import {
	gracefulBase64Decode,
	snakeCaseToTitleCase,
	titleFromIdentifier,
} from '../../../../../utils/shared.utils';
import { uploadDbtManifest } from '../../../../../utils/upload.utils';
import { ImageUpload } from '../../../../ImageUpload/ImageUpload';
import { uploadImagePublic } from '../../../../ImageUpload/ImageUpload.helpers';
import { SmallLoadingSpinner } from '../../../../LoadingSpinner';
import { MultiTeamsSelector } from '../../../../MultiTeamsSelector/MultiTeamsSelector';
import {
	getIdentityTokenIfOidc,
	shouldCancelSyncForOidc,
} from '../../../identityTokenHelpers';
import { Whitelist } from '../../../IntegrationDisclaimers';
import { IntegrationTunnelSelect } from '../../../IntegrationTunnelSelect';
import { SlackOAuthButton } from '../../../SlackOAuthButton';
import ConnectionChoiceList from './ConnectionChoiceList';
import ConnectionTypeRadio from './ConnectionTypeRadio';

export type BuiltInConnectionStepActionsRef = {
	onSubmit: () => void;
};

interface BuiltInConnectionStepProps {
	spec: BuiltinIntegrationSpec;
	integration?: IIntegration;
	setIntegration?: (integration: IIntegration) => void;
	nextStep?: () => void;
	hideTitle?: boolean;
	readonly?: boolean;
	hideSubmit?: boolean;
	hideDetailsSection?: boolean;
	initialTeams?: string[];
	actionsRef?: React.RefObject<BuiltInConnectionStepActionsRef>;
}

const useStyles = createStyles((theme) => ({
	frame: {
		borderRadius: theme.other.borderRadius.md,
		border: `1px solid ${theme.other.getColor('border/secondary/default')}`,
		background: theme.other.getColor('surface/primary/default'),
		padding: theme.spacing.md,
	},
}));

// Constants moved outside component
const BANNER_DOCS_ACTIONS = [
	{
		children: 'Learn more',
		onClick: () => {
			window.open(
				'https://docs.secoda.com/enterprise/self-hosted-secoda/additional-resources/additional-environment-variables',
				'_blank'
			);
		},
	},
];

const WAREHOUSE_TYPES = [
	'bigquery',
	'redshift',
	'snowflake',
	'mssql',
	'mysql',
	'oracle',
	'postgres',
	'databricks',
];

const DBT_TYPES = ['dbt', 'dbt_core'];

const DEFAULT_TOGGLE_STATE = {
	ssl: false,
	set_google_authorization_header: false,
	legacy_authentication: false,
};

// Extract submit label logic
const getSubmitLabel = (
	groupSettings: Record<string, any> | undefined,
	selectedGroup: string,
	testable?: boolean,
	uploadsInProgress?: number,
	integrationLastRunStatus?: string
) => {
	if (uploadsInProgress && uploadsInProgress > 0) {
		return 'Uploading files...';
	}

	if (groupSettings?.[selectedGroup]?.isOAuth) {
		return 'Connect with OAuth';
	}

	if (groupSettings?.[selectedGroup]?.isOidc) {
		return 'Connect with OIDC';
	}

	// Show "Test connection" for new integrations or when last run failed
	const isNewIntegration = !integrationLastRunStatus;
	const lastRunFailed = integrationLastRunStatus === 'failed';

	if (
		(groupSettings?.[selectedGroup]?.testable || testable) &&
		(isNewIntegration || lastRunFailed)
	) {
		return 'Test connection';
	}

	return 'Submit';
};

// Extract form calculation logic
const calculateGroups = (
	form: ObjectSchema<any>,
	groupSettings?: Record<string, any>,
	oauthSupported?: boolean
) => {
	const schemas: Record<string, Record<string, AnySchema>> = {};

	keys(form.fields).forEach((key) => {
		if (key === '_group') return;

		const field = form.fields[key];
		const meta = field.spec?.meta;

		if (meta?.group) {
			if (!schemas[meta.group]) schemas[meta.group] = {};
			schemas[meta.group][key] = field;
		} else if (meta?.groups) {
			meta.groups.forEach((group: string) => {
				if (!schemas[group]) schemas[group] = {};
				schemas[group][key] = field;
			});
		} else {
			if (!schemas.default) schemas.default = {};
			schemas.default[key] = field;
		}
	});

	// Include all groups from groupSettings
	if (groupSettings) {
		keys(groupSettings).forEach((groupName) => {
			if (!schemas[groupName]) {
				schemas[groupName] = {};
			}
		});
	}

	if (oauthSupported) {
		if (!schemas.OAuth) schemas.OAuth = {};
	}

	return schemas;
};

export function BuiltInConnectionStep({
	spec,
	integration,
	setIntegration,
	nextStep,
	hideTitle = false,
	readonly = false,
	hideSubmit = false,
	hideDetailsSection = false,
	initialTeams = [],
	actionsRef,
}: BuiltInConnectionStepProps) {
	const { classes, theme } = useStyles();

	const [error, setError] = useState<string | undefined>();
	const [uploadsInProgress, setUploadsInProgress] = useState<number>(0);
	const [showSslSettings, setShowSslSettings] = useState<boolean>(false);
	const { teamsPermissionsV2Frontend } = useFeatureFlags();
	const { secodaCoreAccess, tunnelAccess } = useSuspenseFeatureAccess();
	const { name, type, groupSettings, oauth, testable } = spec;

	const { user, workspace } = useAuthUser();
	const { data: integrations } = useIntegrationList({});
	const { data: memberEnv } = useSuspenseApiMemberEnv({});

	const { mutateAsync: createIntegration } = useCreateIntegration({
		invalidationKeys: [integrationsQueryKeyFactory.allLists()],
		options: {
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: integrationsQueryKeyFactory.allLists(),
				});
			},
		},
	});
	const { mutateAsync: updateIntegration } = useUpdateIntegration({});

	const navigate = useNavigate();

	const scrollToBottom = useCallback(() => {
		const scrollContainer = document.getElementById('integration-page-scroll');
		if (scrollContainer) {
			scrollContainer.scrollTo({
				top: scrollContainer.scrollHeight,
				behavior: 'smooth',
			});
		}
	}, []);

	const form = useMemo<ObjectSchema<any>>(
		() =>
			integrationSchemas[type as IntegrationSchemaKeys] ||
			Yup.object().shape({}),
		[type]
	);

	const groups = useMemo(
		() => calculateGroups(form, groupSettings, !isNil(oauth)),
		[form, groupSettings, oauth]
	);

	const isRedshiftOrSnowflakeRequired = useMemo(
		() =>
			type === 's3' &&
			!integrations?.results?.some(
				(i) => i.type === 'redshift' || i.type === 'snowflake'
			),
		[integrations?.results, type]
	);

	const isWarehouseRequired = useMemo(
		() =>
			['dbt', 'dbt_cloud'].includes(type) &&
			integrations?.results?.filter((i) => WAREHOUSE_TYPES.includes(i.type))
				.length === 0,
		[integrations?.results, type]
	);

	const isDbtIntegrationRequired = useMemo(
		() =>
			type === 'github' &&
			integrations?.results?.filter((i) => DBT_TYPES.includes(i.type))
				.length === 0,
		[integrations?.results, type]
	);

	const hasMultipleGroups = keys(groups).length > 1;

	const initialValues = {
		name: integration?.name || name,
		teams: integration?.teams || initialTeams || [],
		ssh_tunnel: integration?.ssh_tunnel ?? 'no_tunnel',
		...(integration?.credentials || {}),
	};
	const defaultValues = form?.cast({});
	const defaultNonEmptyValues = omitBy(defaultValues, isNil);

	const [selectedGroup, setSelectedGroup] = useState<string>(
		integration?.credentials?._group ||
			defaultNonEmptyValues._group ||
			keys(groups)[0]
	);
	const selectedSchema = groups[selectedGroup];

	// Check if SSL field exists in the schema
	const hasSslField = useMemo(
		() => secodaCoreAccess && keys(form.fields).includes('ssl'),
		[form.fields, secodaCoreAccess]
	);

	// Initialize SSL toggle state based on existing values or default to true
	const initialSslState = useMemo(() => {
		if (!hasSslField) return false;

		// If integration has SSL enabled, use that
		if (integration?.credentials?.ssl !== undefined) {
			return Boolean(integration.credentials.ssl);
		}

		// Default to true for new integrations
		return true;
	}, [hasSslField, integration?.credentials?.ssl]);

	const [toggleState, setToggleState] = useState<Record<string, boolean>>({
		...DEFAULT_TOGGLE_STATE,
		ssl: initialSslState,
	});

	const fieldsToOmit = useMemo(() => {
		const omittedFields: string[] = [];
		const selectedSchemaFields = keys(selectedSchema);
		keys(form.fields).forEach((field) => {
			if (field !== '_group' && !selectedSchemaFields.includes(field)) {
				omittedFields.push(field);
			}
		});
		return omittedFields;
	}, [form.fields, selectedSchema]);

	const updateOrCreateIntegration = async (values: Record<string, any>) => {
		const { teams } = values;

		// Clear the tunnel values if "No tunnel" is selected
		if (values.ssh_tunnel === 'no_tunnel') {
			if (integration) {
				delete integration.ssh_tunnel;
			}
		}

		// If the integration has not been created yet, create it
		if (!integration) {
			const params = {
				name: values.name || name,
				type,
			};

			// eslint-disable-next-line no-underscore-dangle
			const _integration = await createIntegration({
				data: {
					...params,
					credentials: omit(values, ['teams', 'ssh_tunnel', 'tunnel']),
					teams,
					ssh_tunnel:
						isEmpty(values.ssh_tunnel) ||
						isNil(values.ssh_tunnel) ||
						values.ssh_tunnel === 'no_tunnel'
							? null
							: values.ssh_tunnel,
				},
			});

			// Keep this event for historic activation tracking
			trackEvent(
				'integration/connection/update',
				{
					label: values.name || name,
					type: params.type,
				},
				user,
				workspace!
			);

			setIntegration?.(_integration);
			return _integration;
		}

		const updatedIntegration = await updateIntegration({
			data: {
				id: integration.id,
				name: values.name || name,
				credentials: omit(values, ['teams', 'ssh_tunnel', 'tunnel']),
				teams,
				ssh_tunnel:
					isEmpty(values.ssh_tunnel) ||
					isNil(values.ssh_tunnel) ||
					values.ssh_tunnel === 'no_tunnel'
						? null
						: values.ssh_tunnel,
			},
		});

		setIntegration?.(updatedIntegration);
		return updatedIntegration;
	};

	const handleOAuth = (result: IIntegration) => {
		const baseUrl = api();
		// Use local server tunnel URL for debugging
		// baseUrl =
		// 	'https://e7ad-2607-fea8-fca0-8146-2464-b746-8c6d-7239.ngrok.io/';

		const authUrl = `${baseUrl}oauth/to_oauth/${result.id}/`;
		window.open(authUrl, '_self');
	};

	const updateConnectionSettings = useCallback(
		async (values: any) => {
			setError(undefined);
			try {
				const result = await updateOrCreateIntegration(values);
				let isTestable = testable;

				if (groupSettings && selectedGroup in groupSettings) {
					// Check if the integration is OAuth and handle it
					if (groupSettings[selectedGroup]?.isOAuth) {
						handleOAuth(result);
					}

					// Check if the integration is testable
					if (groupSettings[selectedGroup]?.testable) {
						isTestable = true;
					}
				}

				const isIntegrationConnected = async (
					id: string,
					identityToken?: string
				) => {
					const requestBody = identityToken
						? { identity_token: identityToken }
						: {};
					const res = await apiClient.post(
						`/integration/integrations/${id}/test_connection/`,
						requestBody
					);

					if (res.data.connected) {
						return true;
					}

					throw new Error(`${res.data.formattedError} ${res.data.error}`);
				};

				if (isTestable) {
					const identityToken = await getIdentityTokenIfOidc({
						integrationSpec: {
							type: 'builtin',
							value: spec,
						} as IntegrationSpec,
						selectedGroup,
					});

					const { shouldCancel } = shouldCancelSyncForOidc(identityToken, {
						integrationSpec: {
							type: 'builtin',
							value: spec,
						} as IntegrationSpec,
						selectedGroup,
					});

					if (shouldCancel) {
						setError('Identity token is required to test connection');
						scrollToBottom();
						return;
					}

					const isSuccess = await isIntegrationConnected(
						result.id,
						identityToken
					);

					showNotification({
						title: isSuccess ? 'Connection successful' : 'Connection failed',
						message: isSuccess
							? null // No message if successful
							: 'If the issue persists, please contact support',
						color: isSuccess ? 'green' : 'red',
					});

					if (!isSuccess) {
						setError('Connection error');
						scrollToBottom();
						return;
					}
				}

				setIntegration?.(result);

				// Check if this integration has disablePullMetadata and this is the final step
				if (spec.disablePullMetadata) {
					// Navigate directly to the integration page since sync step is skipped
					navigate(`/integrations/${result.id}`);
				} else {
					nextStep?.();
				}
			} catch (error: any) {
				const errorMessage = JSON.stringify(
					error?.response?.data?.error ?? error?.message ?? error,
					null,
					2
				);
				setError(`Encountered the following error: \n${errorMessage}`);
				scrollToBottom();
			}
		},
		[
			groupSettings,
			nextStep,
			navigate,
			scrollToBottom,
			selectedGroup,
			setIntegration,
			spec.disablePullMetadata,
			testable,
			type,
		]
	);

	const formik = useFormik({
		initialValues: {
			...defaultNonEmptyValues,
			...initialValues,
			// Ensure SSL value is synchronized with toggle state for new integrations
			...(hasSslField &&
				integration?.credentials?.ssl === undefined && {
					ssl: initialSslState,
				}),
		} as any,
		validationSchema: form.omit(fieldsToOmit).concat(
			Yup.object().shape({
				teams: Yup.array(Yup.string()).min(
					1,
					'Please select at least one team'
				),
			})
		),
		validateOnBlur: false,
		validateOnChange: false,
		onSubmit: updateConnectionSettings,
	});

	// Check if SSL is unchecked and no SSH tunnel is selected
	const shouldShowSslWarning = useMemo(
		() =>
			hasSslField &&
			!toggleState.ssl &&
			formik.values.ssh_tunnel === 'no_tunnel',
		[hasSslField, toggleState.ssl, formik.values.ssh_tunnel]
	);

	// Check if SSL values exist and show settings by default if they do
	const hasSslValues = useMemo(() => {
		if (!hasSslField) return false;
		const sslFields = [
			'client_key',
			'client_certificate',
			'certificate_authority',
			'pem_bundle',
		];
		return sslFields.some((field) => {
			const value = formik.values[field];
			return value && typeof value === 'string' && value.trim() !== '';
		});
	}, [hasSslField, formik.values]);

	// Show SSL settings by default if SSL values exist
	// eslint-disable-next-line no-use-effect/no-use-effect
	useEffect(() => {
		if (hasSslField && hasSslValues) {
			setShowSslSettings(true);
		}
	}, [hasSslField, hasSslValues, secodaCoreAccess]);

	const submitLabel = getSubmitLabel(
		groupSettings,
		selectedGroup,
		testable,
		uploadsInProgress,
		integration?.status
	);

	const handleSelectGroup = (group: string) => {
		// Always set _group value when group changes, even if schema doesn't explicitly define it
		formik.setFieldValue('_group', group);
		setSelectedGroup(group);
	};

	const handleTeamsChange = useCallback(
		async (ids: string[]) => {
			formik.setFieldValue('teams', ids);
			if (ids.length === 0) {
				formik.setFieldError('teams', 'Please select at least one team');
				return;
			}
			formik.setFieldError('teams', undefined);
			if (integration) {
				await updateIntegration({
					data: {
						id: integration.id,
						teams: ids,
					},
				});

				showNotification({
					message: teamsPermissionsV2Frontend
						? 'The integration teams has been updated successfully.'
						: 'The integration teams has been updated successfully. Changes may take a few minutes to propagate.',
					color: 'green',
				});
			}
		},
		[formik, integration, updateIntegration, teamsPermissionsV2Frontend]
	);

	const onUpload = useCallback(
		async (file: File) => {
			const result = await uploadImagePublic(file);
			formik.setFieldValue('icon_url', result);
			if (integration) {
				await updateIntegration({
					data: {
						id: integration.id,
						icon_url: result,
					},
				});

				showNotification({
					message: 'The integration logo has been updated successfully',
					color: 'green',
				});
			}
		},
		[formik, integration, updateIntegration]
	);

	useImperativeHandle(actionsRef, () => ({
		onSubmit: () => {
			formik.submitForm();
		},
	}));

	return (
		<>
			{!hideTitle && <Title size="xl">Connect {name}</Title>}
			{isWarehouseRequired && (
				<Banner
					tone="warning"
					message="Please connect a supported data warehouse (Snowflake, Redshift, BigQuery, Postgres, MySQL, MS SQL, Oracle, or Databricks) before configuring dbt. The dbt integration enhances existing table metadata and requires an active warehouse connection."
					title="Data Warehouse Connection Required"
				/>
			)}
			{isRedshiftOrSnowflakeRequired && (
				<Banner
					tone="warning"
					message="A Redshift or Snowflake connection is required before connecting S3. The S3 integration relies on Redshift or Snowflake to query and extract metadata from S3 files."
					title="Redshift or Snowflake Connection Required"
				/>
			)}
			{isDbtIntegrationRequired && (
				<Banner
					tone="warning"
					message="A dbt integration is required before connecting GitHub. GitHub integration relies on dbt model metadata and lineage information."
					title="dbt Integration Required"
				/>
			)}
			{type?.toLowerCase() === 'powerbi' &&
				memberEnv?.POWERBI_ENV_CONFIGURED === false && (
					<Banner
						actions={BANNER_DOCS_ACTIONS}
						tone="warning"
						message="PowerBI environment variables are not configured in your on-premise environment. The integration may not function properly."
						title="Missing PowerBI Configuration"
					/>
				)}
			{type?.toLowerCase() === 'github' &&
				memberEnv?.GITHUB_ENV_CONFIGURED === false && (
					<Banner
						actions={BANNER_DOCS_ACTIONS}
						tone="warning"
						message="GitHub environment variables are not configured in your on-premise environment. The integration may not function properly."
						title="Missing GitHub Configuration"
					/>
				)}
			<form onSubmit={formik.handleSubmit}>
				<Stack spacing="2xl">
					{!hideDetailsSection && (
						<Stack>
							<Title size="md">Details</Title>
							<Stack spacing="md" className={classes.frame}>
								{integration?.type === 'custom' && (
									<Group position="left">
										<ImageUpload
											callback={onUpload}
											label="Integration logo"
											placeholder={
												integration.icon_url ||
												'/images/illustrations/integrations.webp'
											}
											disabled={readonly}
										/>
									</Group>
								)}
								<TextInput
									autoCorrect="off"
									autoComplete="off"
									key="name"
									name="name"
									label="Name"
									placeholder={name}
									value={formik.values.name}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									disabled={readonly}
								/>
								<Stack spacing="3xs">
									<MultiTeamsSelector
										label="Teams"
										value={formik.values.teams}
										setValue={handleTeamsChange}
										required
										error={formik.errors.teams as string}
										disabled={readonly}
										withinPortal={false}
									/>
									<Text size="sm" color="text/secondary/default">
										The selected teams will have access to the integration
										resources
									</Text>
								</Stack>
							</Stack>
						</Stack>
					)}
					<Stack>
						<Title size="md">Connection</Title>
						<Stack spacing="md" className={classes.frame}>
							{hasMultipleGroups && (
								<>
									{keys(groups).length >= 4 ? (
										<ConnectionChoiceList
											title="Method"
											groups={keys(groups)}
											value={selectedGroup}
											onChange={handleSelectGroup}
											disabled={readonly}
										/>
									) : (
										<>
											<TextInputLabel label="Method" inputId="method" />
											<Radio.Group
												value={selectedGroup}
												onChange={handleSelectGroup}
											>
												<Group spacing="xs" noWrap>
													{keys(groups).map((group) => (
														<Stack key={group} style={{ width: '100%' }}>
															<ConnectionTypeRadio
																key={group}
																group={group}
																active={group === selectedGroup}
																selectGroup={handleSelectGroup}
																disabled={readonly}
															/>
														</Stack>
													))}
												</Group>
											</Radio.Group>
										</>
									)}
									{selectedGroup === 'OAuth' &&
										type?.toLowerCase() === 'looker_studio' &&
										memberEnv?.GOOGLEDATASTUDIO_ENV_CONFIGURED === false && (
											<Banner
												actions={BANNER_DOCS_ACTIONS}
												tone="warning"
												message="OAuth integration may not work, as the Looker Studio environment variables are missing from this on-premise environment."
												title="Looker Studio environment variables are missing"
											/>
										)}
									{selectedGroup === 'OAuth' &&
										type?.toLowerCase() === 'bigquery' &&
										memberEnv?.BIGQUERY_ENV_CONFIGURED === false && (
											<Banner
												actions={BANNER_DOCS_ACTIONS}
												tone="warning"
												message="OAuth integration may not work, as the BigQuery environment variables are missing from this on-premise environment."
												title="BigQuery environment variables are missing"
											/>
										)}
								</>
							)}
							<Stack>
								{/* When connecting to an AWS Bucket through AWS IAM Role */}
								{keys(selectedSchema).includes('external_id') && (
									<Box>
										<TextInput
											label="AWS Account ID"
											value={memberEnv?.AWS_ACCOUNT_ID ?? ''}
											disabled
										/>
										<Text size="sm" color="text/secondary/default">
											On the AWS cross account role copy this value to the
											Account ID field
										</Text>
									</Box>
								)}
								{/* 	Filter out SSH Tunnel and OAuth Fields, which will be handled at the end */}
								{keys(selectedSchema)
									.filter((field) => !['ssh_tunnel', 'oauth'].includes(field))
									.filter((field) => {
										if (['ssl'].includes(field)) {
											return secodaCoreAccess;
										}
										return true;
									})
									.map((field) => {
										const spec = selectedSchema[field]?.spec;

										if (spec.meta?.toggleController) {
											const toggle = spec.meta?.toggleController;

											return (
												<Checkbox
													key={toggle}
													name={field}
													label={spec.meta?.label}
													checked={toggleState[toggle]}
													onChange={(event) => {
														setToggleState((prevState) => ({
															...prevState,
															[toggle]: event.target.checked,
														}));
														formik.setFieldValue(
															spec.meta?.toggleController,
															event.target.checked
														);
													}}
													disabled={readonly}
												/>
											);
										}

										// Handle SSL toggle group visibility
										if (spec.meta?.toggleGroup === 'ssl') {
											if (!showSslSettings) {
												return null;
											}
										}

										if (
											spec.meta?.toggleGroup &&
											!toggleState?.[spec.meta?.toggleGroup]
										) {
											return null;
										}

										if (spec.meta?.htmlType === 'file') {
											const label = spec.meta?.label;
											const fieldLabel = field.includes('_')
												? capitalize(field?.replaceAll('_', ' '))
												: capitalize(field);
											return (
												<Box key={field}>
													<FileInput
														key={field}
														name={field}
														w={theme.other.space[60]}
														multiple
														label={label ?? fieldLabel}
														// eslint-disable-next-line @typescript-eslint/ban-ts-comment
														// @ts-ignore poorly typed component
														placeholder={
															formik.values[field] ?? 'Select the file'
														}
														defaultValue={formik.values[field] ?? spec.default}
														onChange={async (files) => {
															if (files.length > 0) {
																if (
																	['great_expectations', 'gx_core'].includes(
																		type
																	)
																) {
																	const reader = new FileReader();
																	reader.onload = async (e) => {
																		formik.setFieldValue(
																			field,
																			e.target?.result
																		);
																	};
																	reader.readAsText(files[0]);
																} else {
																	const fileType = spec.meta?.secodaFileType;
																	setUploadsInProgress(
																		(prevState) => prevState + 1
																	);
																	const fileKey = await uploadDbtManifest(
																		files[0],
																		fileType
																	);

																	if (fileKey !== '') {
																		formik.setFieldValue(field, fileKey);
																	}
																	setUploadsInProgress(
																		(prevState) => prevState - 1
																	);
																}
															}
														}}
														disabled={readonly}
													/>
													<Text size="sm" color="text/secondary/default">
														{spec.meta?.description}
													</Text>
												</Box>
											);
										}

										// Right now, only certificates are used in the textarea.
										// They need to be Base64 encoded, so do not add other fields
										// with textarea type, unless they are expected to be Base64 encoded.
										if (spec.meta?.htmlType === 'textarea') {
											return (
												<TextArea
													autoComplete="off"
													autoCorrect="off"
													key={field}
													name={field}
													minRows={7}
													error={formik.errors[field] as string}
													label={spec.meta?.label ?? titleFromIdentifier(field)}
													help={spec.meta?.description}
													placeholder={spec.meta?.placeholder}
													value={gracefulBase64Decode(
														formik.values[field] ?? ''
													)}
													onChange={(event) =>
														formik.setFieldValue(
															field,
															btoa(event.target.value)
														)
													}
													onBlur={formik.handleBlur}
													onDrop={(event) => {
														event.preventDefault();
														const file = event.dataTransfer.files[0];
														const reader = new FileReader();
														reader.onload = async (e) => {
															formik.setFieldValue(
																field,
																btoa(e.target?.result as string)
															);
														};
														reader.readAsText(file);
													}}
													defaultValue={gracefulBase64Decode(
														formik.values[field] ?? spec.default
													)}
													disabled={readonly}
												/>
											);
										}

										if (selectedSchema[field].type === 'number') {
											return (
												<NumberInput
													key={field}
													name={field}
													label={
														spec.meta?.label ?? snakeCaseToTitleCase(field)
													}
													help={spec.meta?.description}
													value={formik.values[field]}
													error={formik.errors[field] as string}
													defaultValue={spec.default}
													onChange={(value: number | undefined) =>
														formik.setFieldValue(field, value)
													}
													onBlur={formik.handleBlur}
													optional={spec.presence !== 'required'}
													disabled={spec.meta?.disabled || readonly}
													autoComplete="off"
												/>
											);
										}

										return (
											<TextInput
												key={field}
												name={field}
												type={spec.meta?.htmlType}
												label={spec.meta?.label ?? snakeCaseToTitleCase(field)}
												help={spec.meta?.description}
												value={formik.values[field]}
												error={formik.errors[field] as string}
												defaultValue={
													field?.endsWith('external_id')
														? // Obfuscate the workspace_id to use as the `external_id`.
															reverse(workspace.id.split('-')).join('')
														: spec.default
												}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												optional={spec.presence !== 'required'}
												disabled={spec.meta?.disabled || readonly}
												autoComplete="off"
											/>
										);
									})}
								{/* SSL Settings Toggle Button */}
								{hasSslField && toggleState.ssl && (
									<Box>
										<Button
											leftIconName={showSslSettings ? 'minus' : 'plus'}
											variant="tertiary"
											size="sm"
											onClick={() => setShowSslSettings(!showSslSettings)}
											disabled={readonly}
										>
											{showSslSettings ? 'Hide' : 'Add'} self-signed TLS
											configuration
										</Button>
									</Box>
								)}
								{shouldShowSslWarning && (
									<Banner
										tone="warning"
										message="TLS is disabled and no SSH tunnel is configured. To enable secure connections, consider either enabling TLS, using an SSH tunnel, or setting up VPC peering."
										title="Security information"
									/>
								)}
								{tunnelAccess && selectedSchema?.ssh_tunnel && (
									<Suspense fallback={<SmallLoadingSpinner />}>
										<IntegrationTunnelSelect
											formik={formik}
											disabled={readonly}
										/>
									</Suspense>
								)}
								{/* Only applicable to Slack integration */}
								{oauth && (
									<SettingsBlock
										title="Authorization"
										description={oauth.description}
										rightComponent={
											<SlackOAuthButton
												spec={spec as SlackIntegrationSpec}
												disabled={readonly}
											/>
										}
									/>
								)}
							</Stack>
						</Stack>
					</Stack>
					{spec?.suggestWhitelist && (
						<Stack
							spacing="md"
							style={{
								borderRadius: '12px',
								border: '1px solid',
								borderColor: theme.other.getColor('border/secondary/default'),
								background: theme.other.getColor('surface/info/default'),
								paddingLeft: '16px',
								paddingRight: '16px',
							}}
						>
							<Whitelist />
						</Stack>
					)}
					{error && (
						<Banner tone="critical" message={error} title="Connection failed" />
					)}
					{!oauth && !hideSubmit && (
						<Group position="right">
							<Button
								variant="primary"
								size="md"
								loading={formik.isSubmitting}
								type="submit"
								disabled={
									isWarehouseRequired ||
									isDbtIntegrationRequired ||
									isRedshiftOrSnowflakeRequired ||
									uploadsInProgress > 0 ||
									readonly
								}
							>
								{submitLabel}
							</Button>
						</Group>
					)}
				</Stack>
			</form>
		</>
	);
}
